import { IconButton, useRightPane } from "@homebound/beam";
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { PersonalDashboard_TaskFragment } from "src/fragments/circularFragments.generated";
import { Schedule, TaskType } from "src/generated/graphqlTypes";
import { usePersistedToggle } from "src/hooks";
import { useDelayFlagModal } from "src/hooks/useDelayFlagModal";
import { useSaveScheduleTasksMutation } from "src/routes/projects/schedule-v2/ProjectSchedulePageV2.generated";
import { createDraftScheduleUrl } from "src/RouteUrls";
import { openNewTab } from "src/utils/window";
import { useQueryParams } from "use-query-params";
import { StandaloneTaskDetailPane } from "../developments/schedule/components/StandaloneTaskDetailPane";
import { DynamicScheduleSidePane } from "../projects/dynamic-schedules/components/DynamicScheduleSidePane";
import {
  TradePartnerAvailabilityProvider,
  TradePartnerAvailabilityRequestDetailPaneForm,
} from "./components/TradePartnerAvailabilityRequestDetailPane";
import { PersonalDashboardRefetchQueries } from "./enums/consts";
import type { PersonalDashboardFilter } from "./useHeaderFilterDefs";

type PersonalDashboardFilterProps = PropsWithChildren<{
  filter: PersonalDashboardFilter;
  setFilter: (filter: PersonalDashboardFilter) => void;
  selectedTaskId: string | undefined;
  openTaskDetailPane: (task: PersonalDashboard_TaskFragment, tabSelected?: string, scrollIntoViewType?: any) => void;
  toggleFullScreen: () => void;
  fullScreen: boolean;
  openTradePartnerAvailabilityRequestDetailPane: (task: PersonalDashboard_TaskFragment) => void;
  selectedTPAvailabilityRequestTask: string | undefined;
}>;

export const DashboardFilterContext = createContext<PersonalDashboardFilterProps>({
  filter: {},
  setFilter: () => {},
  selectedTaskId: undefined,
  openTaskDetailPane: () => {},
  toggleFullScreen: () => {},
  fullScreen: false,
  openTradePartnerAvailabilityRequestDetailPane: () => {},
  selectedTPAvailabilityRequestTask: undefined,
});

type PersonalDashboardProviderProps = Omit<
  PersonalDashboardFilterProps,
  | "selectedTaskId"
  | "openTaskDetailPane"
  | "toggleFullScreen"
  | "fullScreen"
  | "openTradePartnerAvailabilityRequestDetailPane"
  | "selectedTPAvailabilityRequestTask"
>;

export function PersonalDashboardProvider(props: PersonalDashboardProviderProps) {
  const { children, ...contextValue } = props;
  const [{}, setQueryParams] = useQueryParams({});
  const { openRightPane, closeRightPane } = useRightPane();
  const history = useHistory();
  const { onCompleted } = useDelayFlagModal();
  const [saveScheduleTasks] = useSaveScheduleTasksMutation({ onCompleted });
  const location = useLocation();
  const [fullScreen, toggleFullScreen] = usePersistedToggle("dashboardFullScreen", false);

  // Intentionally not putting the taskId in the query params due to the time based nature of this dashboard,
  // there's no guarantee that the task will still be in the filtered view if the user is linked.
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined);
  // Creating a separate state to hold the availability request tasks so users can switch between the request pane and details pane
  const [selectedTPAvailabilityRequestTask, setSelectedTPAvailabilityRequestTask] = useState<string | undefined>(
    undefined,
  );

  const openTradePartnerAvailabilityRequestDetailPane = useCallback(
    (task: PersonalDashboard_TaskFragment) => {
      const { id } = task;
      setSelectedTPAvailabilityRequestTask(id);
      setSelectedTaskId(undefined);
      openRightPane({
        content: (
          <TradePartnerAvailabilityProvider
            onClose={() => {
              closeRightPane();
              setSelectedTPAvailabilityRequestTask(undefined);
              setSelectedTaskId(undefined);
            }}
            scheduleTask={task}
          >
            {/* Adding a key to force react to re-create the form when the task changes */}
            <TradePartnerAvailabilityRequestDetailPaneForm key={task.id} />
          </TradePartnerAvailabilityProvider>
        ),
      });
    },
    [closeRightPane, openRightPane],
  );

  const openTaskDetailPane = (task: PersonalDashboard_TaskFragment, tabSelected?: string, scrollIntoViewType?: any) => {
    // For H1 tech projects, navigate to the task details page on the dynamic schedule
    if (task.project?.enableProductConfigPlan) {
      history.push(createDraftScheduleUrl(task.project.id, task.id));
      return;
    }

    setSelectedTaskId(task.id);
    setSelectedTPAvailabilityRequestTask(undefined);
    const { id, schedule, type } = task;

    // Required for plan tasks to open the sidebar
    if (type.code === TaskType.PlanTask) setQueryParams({ paneTaskId: task.id });

    openRightPane({
      content:
        type.code === TaskType.ScheduleTask ? (
          <StandaloneTaskDetailPane
            taskId={id}
            scheduleId={schedule.id}
            stage={(schedule as Schedule).stage}
            onClose={() => {
              closeRightPane();
              setSelectedTaskId(undefined);
              setSelectedTPAvailabilityRequestTask(undefined);
            }}
            onSave={async (changedValues) => {
              await saveScheduleTasks({
                variables: { input: { tasks: [{ ...changedValues, id: task.id }] } },
                refetchQueries: PersonalDashboardRefetchQueries,
              });
            }}
            disableQueryParams
            headerActions={
              <IconButton
                data-testid="externalIcon"
                icon="linkExternal"
                onClick={() => openNewTab(task.blueprintUrl.url)}
                inc={2}
              />
            }
            taskPaneState={{
              scrollIntoViewType,
              tab: tabSelected || "details",
              taskPaneId: task.id,
            }}
          />
        ) : (
          <DynamicScheduleSidePane
            onClose={() => {
              closeRightPane();
              setSelectedTaskId(undefined);
              setSelectedTPAvailabilityRequestTask(undefined);
            }}
            draftTaskId={task.id}
            scheduleParentId={task.project!.id}
          />
        ),
    });
  };

  // The task sidebar only really applies to a few views, so we want to close it when the user navigates to another tab/page.
  // We intentionally watch `location.pathname` rather than the full `location` object because `openTaskDetailPane` calls
  // `setQueryParams` to set the paneTaskId. Watching the full `location` would cause this cleanup to run immediately
  // after opening the pane (since query param changes update the location object), closing it before the user can see it.
  useEffect(() => {
    return () => {
      closeRightPane();
      setSelectedTaskId(undefined);
      setSelectedTPAvailabilityRequestTask(undefined);
    };
  }, [location.pathname, closeRightPane]);

  return (
    <DashboardFilterContext.Provider
      value={{
        ...contextValue,
        selectedTaskId,
        openTaskDetailPane,
        fullScreen,
        toggleFullScreen,
        openTradePartnerAvailabilityRequestDetailPane,
        selectedTPAvailabilityRequestTask,
      }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
}

DashboardFilterContext.displayName = "DashboardFilterContext";

export function useDashboardFilterContext() {
  return useContext(DashboardFilterContext);
}
